import {useEffect, useState} from 'react';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {IFormData} from 'presentation/pages/public/sign-up/components/Forms/Step2/schema';
import {translations} from 'presentation/translations-new';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';
import {APIErrorsMessage} from '../../constants/APIErrorsMessage';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {signUpSendToken} from '../../services/signUpSendToken';
import {useSignUpFormDataStore} from '../../sign-up/store/useSignUpFormDataStore';
import {useSteps} from '../useSteps';
import {UseCheckPersonalFieldsProps} from './types';

const {serverError} = translations['pt-br'].commons;
const {invalidBirthday, invalidEmail} = translations['pt-br'].publicFlow.APIErrors;

export function useCheckPersonalFields({methods}: UseCheckPersonalFieldsProps) {
	const {
		getValues,
		formState: {isValidating},
	} = methods;
	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const {handleNextStep} = useSteps(PublicFlowEnum.SIGN_UP);
	const signUpFormData = useSignUpFormDataStore(state => state.signUpFormData);
	const setSignUpFormData = useSignUpFormDataStore(state => state.setSignUpFormData);
	const {cdUsuario, cpf, identificador, cdEmpresaPlano} = signUpFormData;
	const formFieldValues = getValues();
	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;

	const handleCheckPersonalData = async ({
		birthday,
		cellphone,
		confirmEmail,
		email,
	}: IFormData) => {
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			const {cdPessoa} = await signUpSendToken({
				cdUsuario,
				cpf,
				cdEmpresaPlano,
				dtNascimento: birthday,
				celular: cellphone,
				dsEmailLogin: email,
				dsEmailLoginConfirmacao: confirmEmail,
				identificador,
			});
			setRequestState(OperationStateTypesEnum.DONE);
			setSignUpFormData(state => ({
				...state,
				cdPessoa,
				celular: cellphone,
				dsEmailLogin: email,
			}));

			handleNextStep();
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (
					error.statusCode === HttpStatusCode.unprocessable &&
					error.message.includes(APIErrorsMessage.INVALID_BIRTHDAY)
				) {
					setApiErrorMsg(invalidBirthday);
				}
				if (
					error.statusCode === HttpStatusCode.unprocessable &&
					error.message.includes(APIErrorsMessage.INVALID_EMAIL)
				) {
					setApiErrorMsg(invalidEmail);
				}

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleCheckPersonalData,
		apiErrorMsg,
		showAPIError,
	};
}
